import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useGetFeatureFlagsQuery } from "./features-generated.hooks";

type FeatureFlagContextProps = {
  enabledFeatures: string[];
  isFeatureEnabled: (featureName: string) => boolean;
};

export const FeatureFlagContext = createContext({
  enabledFeatures: [],
  isFeatureEnabled: () => false,
} as FeatureFlagContextProps);

type FeatureFlagContextProviderProps = {
  children: ReactNode;
};

const LOCAL_STORAGE_KEY = "graphFeatureFlag";
const TIMESTAMP_KEY = "graphFeatureFlagTimestamp";
const CACHE_DURATION = 60000; // 60 seconds
const DEFAULT_ENABLED_FEATURES = [];

export const useFeatureFlag = (): FeatureFlagContextProps => {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error(
      "useFeatureFlag must be inside a FeatureFlagContextProvider",
    );
  }

  return context;
};

const FeatureFlagContextProvider = ({
  children,
}: FeatureFlagContextProviderProps) => {
  const { data } = useGetFeatureFlagsQuery({ skip: shouldSkipQuery() });

  // Helper function to determine if we should skip the query
  function shouldSkipQuery(): boolean {
    const timestamp = localStorage.getItem(TIMESTAMP_KEY);
    if (timestamp) {
      const elapsedTime = Date.now() - Number(timestamp);
      return elapsedTime < CACHE_DURATION;
    }
    return false;
  }

  useEffect(() => {
    if (data?.getFeatureFlags) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify(data?.getFeatureFlags),
      );
      localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
    }
  }, [data?.getFeatureFlags]);

  const enabledFeatures = useMemo(() => {
    if (data?.getFeatureFlags) return data.getFeatureFlags;

    const cachedFeatureFlag = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (cachedFeatureFlag) {
      return JSON.parse(cachedFeatureFlag);
    } else {
      return DEFAULT_ENABLED_FEATURES;
    }
  }, [data?.getFeatureFlags]);

  const isFeatureEnabled = useCallback(
    (featureName: string) => !!enabledFeatures.includes(featureName),
    [enabledFeatures],
  );

  return (
    <FeatureFlagContext.Provider
      value={{
        enabledFeatures,
        isFeatureEnabled,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export default FeatureFlagContextProvider;
